import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { global } from "@shared/libs/Global";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  CellValueChangedEvent,
  RowData,
} from "@shared/components/GridV2/SnGridTypes";
import { ColDef, RowNode } from "ag-grid-community";
import SearchPanel from "@widgets/SearchPanel";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnTree from "@shared/components/GridV2/SnTree";
import SnButton from "@shared/components/SnButton";

interface Props {
  Menuprops: menuInfo;
}

const SN_PMS_BAS_REG_007 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    tree1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    tree1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    tree1: [],
  });

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1,
          tree1: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async () => {
    try {
      const modifiedRows = gridRefs.current["tree1"].current.getChange();
      if (modifiedRows.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const isSaved = await onSave();
          if (!isSaved) return;
        }
      }

      const param = {
        id: "sn_pms_bas_reg_007_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["tree1"].current.resetUpdate();
      const data: any = await fnGetDataSet(param);
      setDataSources({
        grid1: data[0],
        tree1: data[1] || [],
      });
    } catch (error) {}
  };

  const onInsert = () => {
    try {
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("alter", "수정된 내용이 있습니다.");
        return;
      }

      const newRow = {
        cd_firm: userInfo?.cd_firm,
        cd_user_reg: userInfo?.cd_user,
        fg_pjt: 1,
        yn_use: "Y",
      };
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (!focusedNodeH) {
        showDialog("alter", "선택된 템플릿이 없습니다.");
        return;
      }

      if (!focusedNodeH.data.cd_template) {
        showDialog("alter", "템플릿 코드를 입력해주세요.");
        return;
      }

      const rowCount = gridRefs.current["tree1"].current.getRowCount();
      const maxNoLine =
        gridRefs.current["tree1"].current.getMaxValue("no_line");

      // 신규 행 기본 데이터
      const newRow: RowData = {
        cd_firm: userInfo.cd_firm,
        cd_template: focusedNodeH.data.cd_template,
        no_line: maxNoLine + 1,
        no_level: 1,
        cd_bs_p: null,
      };

      if (rowCount === 0) {
        // 아무 행도 없음 -> 1로 추가
        newRow.no_seq_sort = 1;
        newRow.cd_level = "1.1";
        const addedNode = gridRefs.current["tree1"].current?.addRow(
          newRow,
          null
        );
        gridRefs.current["tree1"].current.applyFilters({
          cd_template: focusedNodeH.data.cd_template,
        });
        gridRefs.current["tree1"].current.selectAndFocusRow(addedNode.rowIndex);
        return;
      }

      // 선택된 노드가 있는 경우
      const firstList: any[] = [];
      gridRefs.current["tree1"].current.forEachFilteredNode((node: RowNode) => {
        if (node.data.no_level == 1) firstList.push(node);
      });

      newRow.no_seq_sort = firstList.length + 1;
      newRow.cd_level = "1." + (firstList.length + 1);

      const addedNode = gridRefs.current["tree1"].current?.addRow(newRow, null);
      gridRefs.current["tree1"].current.applyFilters({
        cd_template: focusedNodeH.data.cd_template,
      });
      gridRefs.current["tree1"].current.selectAndFocusRow(addedNode.rowIndex);
    } catch (error) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["tree1"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["tree1"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    // 선택 행 삭제
    await gridRefs.current["tree1"].current?.deleteSelectedRows();

    // 삭제 후 같은 부모 기준으로 no_seq_sort 재계산
    const treeIdKey = "cd_bs";
    const parentKey = "cd_bs_p";
    const orderKey = "no_seq_sort";

    const groupMap = new Map<string | null, RowNode[]>();
    gridRefs.current["tree1"].current.forEachFilteredNode((node: RowNode) => {
      const parentId = node.data[parentKey] ?? null;
      if (!groupMap.has(parentId)) {
        groupMap.set(parentId, []);
      }
      groupMap.get(parentId)!.push(node);
    });

    // 순번 재계산
    groupMap.forEach((nodes) => {
      nodes.sort((a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0));
      nodes.forEach((node, idx) => {
        const newSeq = idx + 1;
        const oldSeq = node.data[orderKey];

        // no_seq_sort 변경
        if (oldSeq !== newSeq) {
          node.setDataValue(orderKey, newSeq);
        }
      });
    });
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["tree1"].current?.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["tree1"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const pmsCbsHChanges = gridRefs.current["grid1"].current.getChange();
      const pmsCbsLChanges = gridRefs.current["tree1"].current.getChange();
      const lineInserts = pmsCbsLChanges.insert ?? [];
      if (pmsCbsHChanges.insert.length > 0 && lineInserts === 0) {
        await showDialog(
          "alter",
          "CB정보가 없습니다.\n행추가하여 CB정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        pmsCbsHChanges.modifiedRowcount === 0 &&
        pmsCbsLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const tempBsMap = new Map<string, string>();
      for (const row of lineInserts) {
        const cd_bs = row.cd_bs;
        if (cd_bs?.includes("temp-")) {
          const realCdBs = `${row.cd_template}_${row.no_line}`;
          tempBsMap.set(row.cd_bs, realCdBs);
        }
      }

      for (const row of lineInserts) {
        // 본인 cd_bs 치환
        if (row.cd_bs && tempBsMap.has(row.cd_bs)) {
          row.cd_bs = tempBsMap.get(row.cd_bs);
        }

        // 부모 cd_bs_p 치환
        if (row.cd_bs_p && tempBsMap.has(row.cd_bs_p)) {
          row.cd_bs_p = tempBsMap.get(row.cd_bs_p);
        }
      }

      const ok = await fnSave("SN_PMS_BAS_REG_007_SAVE_01", {
        pms_cbsh: pmsCbsHChanges,
        pms_cbsl: pmsCbsLChanges,
      });
      if (!ok) return false;

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["tree1"].current.resetUpdate();
      return true;
    } catch (error) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["tree1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id === "btnOpen") {
      gridRefs.current["tree1"].current.setExpandedAll(true);
    }
    if (id === "btnClose") {
      gridRefs.current["tree1"].current.setExpandedAll(false);
    }
    if (id === "btnAddRow") {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (!focusedNodeH) {
        showDialog("alter", "선택된 템플릿이 없습니다.");
        return;
      }

      if (!focusedNodeH.data.cd_template) {
        showDialog("alter", "템플릿 코드를 입력해주세요.");
        return;
      }

      const selectedNodes =
        gridRefs.current["tree1"].current.getSelectedNodes();
      const selectedNode =
        selectedNodes && selectedNodes.length > 0 ? selectedNodes[0] : null;

      if (!selectedNode) {
        await showDialog("alter", "상위 CB를 선택해주세요.");
        return;
      }

      const selectedNodeChildren = selectedNode.childrenAfterGroup || [];
      const maxNoLine =
        gridRefs.current["tree1"].current.getMaxValue("no_line");
      const maxNoSeqSort =
        selectedNodeChildren.length > 0
          ? Math.max(
              ...selectedNodeChildren.map(
                (child: any) => child.data.no_seq_sort || 0
              )
            )
          : 0;

      // 닫혀 있으면 열기
      if (!selectedNode.expanded) {
        selectedNode.setExpanded(true);
        gridRefs.current["tree1"].current.onGroupExpandedOrCollapsed();
      }

      // 신규행 구성
      const newRow: RowData = {
        cd_firm: userInfo.cd_firm,
        cd_template: selectedNode.data.cd_template,
        no_line: maxNoLine + 1,
        cd_bs_p: selectedNode.data.cd_bs,
        no_level: selectedNode.data.no_level + 1,
        no_seq_sort: maxNoSeqSort + 1,
        cd_level: selectedNode.data.cd_level + "." + (maxNoSeqSort + 1),
      };
      const addedNode = gridRefs.current["tree1"].current.addRow(
        newRow,
        selectedNode
      );
      gridRefs.current["tree1"].current.applyFilters({
        cd_template: selectedNode.data.cd_template,
      });
      gridRefs.current["tree1"].current.selectAndFocusRow(addedNode.rowIndex);
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      // 배열로 만들어서 필터링 함수 호출
      const cd_template = selectedRow.cd_template;
      gridRefs.current["tree1"].current.applyFilters({
        cd_template: cd_template,
      });
      gridRefs.current["tree1"].current.selectAndFocusRow(0);
    } else {
      return;
    }
  };

  const onCellValueChanged2 = (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "no_seq_sort") {
      const newSeq = rowData.no_seq_sort;
      const parts = rowData.cd_level.split(".");
      const lastIdx = parts.length - 1;
      if (parts[lastIdx] !== String(newSeq)) {
        parts[lastIdx] = String(newSeq);
        rowNode.setDataValue("cd_level", parts.join("."));
      }
    }
    if (colId === "cd_level") {
      const oldCdLevel = event.oldValue;
      const newCdLevel = event.newValue;

      if (!oldCdLevel || !newCdLevel) return;
      if (oldCdLevel === newCdLevel) return;

      const parentId = rowData.cd_bs;
      gridRefs.current["tree1"].current.forEachFilteredNode(
        (childNode: RowNode) => {
          if (childNode.data.cd_bs_p === parentId) {
            const childLevel = childNode.data.cd_level;

            if (childLevel && childLevel.startsWith(oldCdLevel + ".")) {
              const suffix = childLevel.slice(oldCdLevel.length);
              const newChildLevel = newCdLevel + suffix;

              if (childNode.data.cd_level !== newChildLevel) {
                childNode.setDataValue("cd_level", newChildLevel);
              }
            }
          }
        }
      );
    }
  };

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      headerName: "Level",
      field: "cd_level",
      minWidth: 100,
      cellRendererParams: {
        suppressCount: true, // 하위 아이템 개수 숨기기
        suppressDoubleClickExpand: true, // 더블 클릭으로 확장/축소 비활성화
        suppressHoverHighlight: false,
      },
      rowDrag: true,
    };
  }, []);

  const getRowId = useCallback((params: any) => String(params.data.cd_bs), []);

  const onRowDataReady = () => {
    gridRefs.current["tree1"].current.selectAndFocusRow(0);
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
        },
      },
      tree1: {
        columns: columns.tree1,
        dataSource: dataSources.tree1,
        events: {
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onRowDataReady: onRowDataReady,
          onCellValueChanged: onCellValueChanged2,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          
        />
      </div>

      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="35%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "템플릿정보" }}
            events={grids.grid1.events}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="64.5%">
          <SnTree
            ref={gridRefs.current["tree1"]}
            columnConfig={grids.tree1.columns}
            rowData={grids.tree1.dataSource}
            etcProps={{
              isEditable: true,
              rowSelection: "single",
              title: "CB정보",
            }}
            header={
              <div>
                <SnButton
                  text="전체전개"
                  onClick={() => onGrid1BtnsClick("btnOpen")}
                />
                <SnButton
                  text="전체닫기"
                  onClick={() => onGrid1BtnsClick("btnClose")}
                />
                <SnButton
                  text="하위레벨추가"
                  onClick={() => onGrid1BtnsClick("btnAddRow")}
                />
              </div>
            }
            events={grids.tree1.events}
            treeProps={{
              treeDataParentIdField: "cd_bs_p",
              treeIdKey: "cd_bs",
              treeOrderKey: "no_seq_sort",
              groupDefaultExpanded: -1,
              autoGroupColumnDef: autoGroupColumnDef,
              getRowId: getRowId,
            }}
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_PMS_BAS_REG_007;
